import React, { useState, FC, useEffect, useContext } from "react";
import { MatterTable, MatterFilter, MatterRecordType, MatterStatus, AscDesc } from "@design-system";
import { Link, useNavigate } from "react-router-dom";
import { Tooltip } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { filterTitle, filterClient, filterUser, filterUpdatedAt } from "./filter";
import { UPDATE_MATTER } from "../../graphql/mutation";
import { FIND_ACCRUED_FEES_FOR_USER, NOTIFICATIONS_QUERY } from "../../graphql/query";
import { useMutation } from "../../hooks";
import { useFilteredMattersLazyQuery } from "../../hooks/matters";
import { UPDATE_PARENT_MATTER } from "../parent-matter/order-parents-hook";
import { UpdateParentMatterMutation, UpdateParentMatterMutationVariables } from "~/gql/graphql";
import { useQuery } from "@apollo/client";
import { USER_DATA_QUERY } from "~/hooks/user-data/user-data";
import { GlobalContext } from "~/context/global-context";

export const INITIAL_MATTER_FILTER: MatterFilter = {
  title: null,
  client: null,
  orgUnit: null,
  currencyId: null,
  status: null,
  updatedAt: null,
  orderBy: [{ updatedAt: AscDesc.desc }],
  createdBy: null,
  currentPage: 1,
  pageSize: 10,
  feeEarner: null,
  someTimeEntries: null,
  linked: null,
};

type MatterTableContainerProps = {
  status: MatterStatus;
};

export const MatterTableContainer: FC<MatterTableContainerProps> = ({ status }) => {
  const currentUserInfo = useContext(GlobalContext);

  const [filterData, setFilter] = useState<MatterFilter>({
    ...INITIAL_MATTER_FILTER,
    status: [status],
  });

  const { loading: loadingUserData } = useQuery(USER_DATA_QUERY, {
    onCompleted: (data) => {
      if (!data?.currentUserFeeEarnerData?.userId) return;
      setFilter((curr) => ({
        ...curr,
        createdBy: [data?.currentUserFeeEarnerData?.userId!, ...(filterData.createdBy ?? [])],
      }));
    },
  });

  const { pageSize, currentPage, ...filter } = filterData;

  const { callFunc, data, loading, error } = useFilteredMattersLazyQuery({
    filter: {
      ...(filter?.title && { title: filter.title }),
      ...(filter?.client && { clientId: filter.client }),
      ...(filter?.status && { status: filter.status }),
      ...(filter?.updatedAt?.from && {
        updatedAtFrom: filter.updatedAt?.from,
      }),
      ...(filter?.updatedAt?.to && {
        updatedAtTo: filter.updatedAt?.to,
      }),
      ...(filter?.createdBy && { createdBy: filter.createdBy }),
      template: false,
    },
    ...(filter?.orderBy && { orderBy: filter.orderBy }),
    offset: ((currentPage || 1) - 1) * (pageSize || 0),
    limit: pageSize,
  });

  useEffect(() => {
    if (!loadingUserData && (filterData.createdBy?.length ?? 0) > 0) {
      callFunc();
    }
  }, [loadingUserData, filterData]);

  const [doUpdateParent, { loading: loadingUpdateParent }] = useMutation<
    UpdateParentMatterMutation,
    UpdateParentMatterMutationVariables
  >(UPDATE_PARENT_MATTER, {
    awaitRefetchQueries: true,
    refetchQueries: () => [
      {
        query: FIND_ACCRUED_FEES_FOR_USER,
        variables: {
          filter: {
            matterStatus: MatterStatus.Live,
          },
        },
      },
      {
        query: NOTIFICATIONS_QUERY,
        variables: { type: "Alert" },
      },
    ],

    update: (cache) => {
      cache.evict({
        id: "ROOT_QUERY",
        fieldName: "filteredMatters",
      });
    },
  });

  const [doUpdateMatter, { loading: loadingUpdateMatter }] = useMutation(UPDATE_MATTER, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: FIND_ACCRUED_FEES_FOR_USER,
        variables: {
          filter: {
            matterStatus: MatterStatus.Live,
          },
        },
      },
      {
        query: NOTIFICATIONS_QUERY,
        variables: { type: "Alert" },
      },
    ],
    update: (cache) => {
      cache.evict({
        id: "ROOT_QUERY",
        fieldName: "filteredMatters",
      });
      cache.evict({
        id: "ROOT_QUERY",
        fieldName: "findAccruedFeesForUser",
      });
    },
  });

  const navigate = useNavigate();

  const updateMatter = async ({
    status,
    id,
    isParent,
  }: Pick<MatterRecordType, "status" | "id" | "isParent">): Promise<void> => {
    if (isParent) {
      await doUpdateParent({
        variables: {
          updateParentMatterId: id,
          updateInput: {
            status: { set: status },
          },
        },
      });
    } else {
      await doUpdateMatter({
        variables: {
          id,
          data: {
            status: { set: status },
          },
        },
      });
    }
  };

  const deleteMatter = async ({
    id,
    isParent,
  }: Pick<MatterRecordType, "id" | "isParent">): Promise<void> => {
    if (isParent) {
      await doUpdateParent({
        variables: {
          updateParentMatterId: id,
          updateInput: {
            deletedAt: { set: new Date() },
            deletedBy: {
              connect: { id: currentUserInfo?.currentUser?.userId },
            },
          },
        },
      });
    } else {
      await doUpdateMatter({
        variables: {
          id,
          data: {
            deletedAt: { set: new Date() },
            deletedBy: {
              connect: { id: currentUserInfo?.currentUser?.userId },
            },
          },
        },
      });
    }
  };

  return (
    <div>
      {error && <h2>Error: {error.message}</h2>}
      <MatterTable
        matters={(data?.filteredMatters.matters as unknown as MatterRecordType[]) || []}
        loading={loading || loadingUpdateParent || loadingUpdateMatter || loadingUserData}
        onChange={(data) => {
          if (data.createdBy?.length) {
            return setFilter(data);
          }
          return setFilter((curr) => ({ ...data, createdBy: curr.createdBy }));
        }}
        filterData={filterData}
        filters={{
          title: filterTitle,
          client: filterClient,
          updatedAt: filterUpdatedAt,
          createdBy: filterUser,
        }}
        renderActions={(record) => (
          <Tooltip title={"Edit Matter"}>
            <Link to={`/matter-editor/${record.id}`}>
              <EditOutlined />
            </Link>
          </Tooltip>
        )}
        onRowClick={(record) => {
          navigate(`/${record.isParent ? "parent" : "matter-editor"}/${record.id}`);
        }}
        updateMatter={updateMatter}
        deleteMatter={deleteMatter}
        pagination={{
          current: currentPage || 1,
          pageSize: pageSize || 0,
          total: data?.filteredMatters.count || 0,
        }}
      />
    </div>
  );
};
